import { useEffect, useState } from 'react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Typography, CircularProgress, Box, TableContainer, Paper } from '@material-ui/core';
import { settingsPageTranslationRef } from '../../translation/settings-page/settings-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';


interface ProjectOrAppTableProps {
  type: 'project' | 'application';
  title: string;
  rolesNames: string[]; // Array of roles to check
}

const ProjectOrAppTable = ({ type, title, rolesNames }: ProjectOrAppTableProps) => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const config = useApi(configApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const { t } = useTranslationRef(settingsPageTranslationRef);

  const [entities, setEntities] = useState<any[]>([]);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchEntitiesAndRoles = async () => {
      setLoading(true);
      try {
        // --- Obtener usuario actual (email o username) ---
        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;
        let userEmail = userEntityRef.split('/')[1];
        try {
          const userEntity = await catalogApi.getEntityByRef(userEntityRef);
          const profile = userEntity?.spec?.profile as any;
          if (profile?.email) userEmail = profile.email;
        } catch { }
        // Impersonación
        const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
        const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
        const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
        let effectiveUser = impersonatedUser || userEmail;
        if (impersonatedUser && impersonatedUser !== userEmail) {
          try {
            const impersonatedUserRef = `user:default/${impersonatedUser}`;
            const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
            const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
            if (impersonatedProfile?.email) effectiveUser = impersonatedProfile.email;
          } catch { }
        }
        const token = (await identityApi.getCredentials()).token;

        // --- Obtener entidades y roles por cada rol ---
        // Map: code -> role
        const codeToRole: Record<string, string> = {};
        const codeSet = new Set<string>();
        for (const role of rolesNames) {
          const resp = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ user: 'user:' + effectiveUser, type, relation: role }),
          });
          const data = await resp.json();
          console.log(`OpenFGA list-user-entities ${role} response:`, data);
          const codes = (data.objects ?? []).map((p: string) => {
            const match = type === 'project'
              ? p.match(/^project:(prj\d{7})$/i)
              : p.match(/^application:(apm\d{7})$/i);
            return match ? match[1].toLowerCase() : null;
          }).filter(Boolean);
          codes.forEach((code: string) => {
            codeSet.add(code);
            // Si ya tiene un rol, prioriza el primero en el array roles
            if (!codeToRole[code]) {
              codeToRole[code] = role;
            }
          });
        }

        // Obtener nombres desde el catálogo para cada código
        const numbers = Array.from(codeSet);
        setEntities(numbers);
        setRoles(codeToRole);

        // Buscar nombres en el catálogo en una sola llamada
        const fetchedNames: Record<string, string> = {};
        let allEntities;
        if (type === 'project') {
          allEntities = await catalogApi.getEntities({ filter: [{ kind: 'Project' }] });
        } else {
          allEntities = await catalogApi.getEntities({ filter: [{ kind: 'Application' }] });
        }
        // Filtrar por los códigos obtenidos
        allEntities.items.forEach((entity: any) => {
          const code = (type === 'project') ? (entity.spec?.number?.toLowerCase()) : (entity.spec?.number?.toLowerCase());
          if (code && numbers.includes(code)) {
            fetchedNames[code] = entity.metadata?.title || '';
          }
        });
        // Para los códigos que no tengan nombre, poner vacío
        numbers.forEach(code => {
          if (!fetchedNames[code]) fetchedNames[code] = '';
        });
        setNames(fetchedNames);
      } finally {
        setLoading(false);
      }
    };
    fetchEntitiesAndRoles();
  }, [identityApi, catalogApi, config, backendUrl, type]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} style={{ marginTop: 16 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("permissions.table.code")}</TableCell>
                <TableCell>{t("permissions.table.name")}</TableCell>
                <TableCell align='right'>{t("permissions.table.role")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((number: string) => (
                <TableRow key={number}>
                  <TableCell>{number.toUpperCase()}</TableCell>
                  <TableCell>{names[number] || '—'}</TableCell>
                  <TableCell align='right'>{roles[number] || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={entities.length}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Box>
  );
};

export default ProjectOrAppTable;
